import React from 'react';
import { Component } from '../../../types';
import { Checklist } from '../../progress/Checklist';
import { useQueryTemplateData, isQueryTemplateDataSource } from '../../../hooks/useQueryTemplateData';
import { ChartDataSource } from '../../../types/chartDataSource';
import { pmTheme } from '../../pm/pmTheme';

export interface SharedChecklistComponentProps {
  component: Component;
  isViewerMode?: boolean;
}

const CHART_DATA_SOURCE_TYPES = new Set<string>([
  'static',
  'query-template',
  'timestamp-aggregate',
  'workflow',
  'composite-query',
  'home-assistant-placeholder',
]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isChartDataSource(value: unknown): value is ChartDataSource {
  return isRecord(value)
    && typeof value.type === 'string'
    && CHART_DATA_SOURCE_TYPES.has(value.type);
}

function getRecordArray(value: unknown): Record<string, unknown>[] | undefined {
  if (!Array.isArray(value)) {
    return undefined;
  }

  const records = value.filter(isRecord);
  return records.length === value.length ? records : undefined;
}

export const SharedChecklistComponent: React.FC<SharedChecklistComponentProps> = ({
  component,
  isViewerMode = false,
}) => {
  const { properties = {} } = component;
  const dataSource = isChartDataSource(properties.dataSource) ? properties.dataSource : undefined;
  const sampleData = getRecordArray(properties.sampleData);
  const hasQueryTemplate = isQueryTemplateDataSource(dataSource);

  const {
    data: queryTemplateData,
    isLoading,
    error,
    refetch,
  } = useQueryTemplateData({
    dataSource,
    componentId: component.id,
    enabled: hasQueryTemplate,
    initialData: sampleData,
  });

  const resolvedData: Record<string, unknown>[] | undefined =
    (hasQueryTemplate ? queryTemplateData : undefined)
    ?? sampleData;

  if (isLoading && !queryTemplateData && hasQueryTemplate) {
    return (
      <div
        data-testid="checklist-loading"
        style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, color: pmTheme.textMuted, fontSize: 13 }}
      >
        <svg className="animate-spin" style={{ width: 20, height: 20 }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21 12a9 9 0 11-6.219-8.56" />
        </svg>
        Loading checklist data...
      </div>
    );
  }

  if (error && !queryTemplateData && hasQueryTemplate) {
    return (
      <div
        data-testid="checklist-error"
        style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8 }}
      >
        <span style={{ fontSize: 13, color: pmTheme.danger }}>{error}</span>
        <button
          onClick={() => refetch()}
          style={{ fontSize: 12, color: pmTheme.primary, background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <Checklist
      component={component}
      data={resolvedData}
      isViewerMode={isViewerMode}
    />
  );
};

SharedChecklistComponent.displayName = 'SharedChecklistComponent';
